import { useMemo, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { submitQuiz } from "@/lib/api/stories"
import { processStoryHtml } from "@/lib/furigana-processor"
import type { FuriganaMode } from "@/hooks/use-furigana-preference"
import type { QuizQuestion, QuizResult } from "@yomimono/types"

interface StoryQuizProps {
  storyId: string
  questions: QuizQuestion[]
  result: QuizResult | null
  furiganaMode: FuriganaMode
  furiganaFirstOnly: boolean
  knownKanji: string[]
}

function optionClass(
  isPicked: boolean,
  isCorrect: boolean,
  revealed: boolean
): string {
  if (!revealed) {
    return "border-input bg-card cursor-pointer hover:border-foreground/30 hover:shadow-sm dark:bg-[var(--surface-input)]"
  }
  // Only reveal correctness for the option the user actually picked: a correct
  // pick turns green, a wrong pick turns red. We don't point out the right
  // answer when they got it wrong.
  if (isPicked && isCorrect) {
    return "border-success bg-success-soft"
  }
  if (isPicked) {
    return "border-destructive bg-destructive-soft"
  }
  return "border-input bg-background opacity-60 dark:bg-[var(--surface-input)]"
}

export function StoryQuiz({
  storyId,
  questions,
  result,
  furiganaMode,
  furiganaFirstOnly,
  knownKanji,
}: StoryQuizProps) {
  const queryClient = useQueryClient()

  const correctIndices = questions.map((q) => q.correctIndex)

  const processedQuestions = useMemo(
    () =>
      questions.map((q) => ({
        question: processStoryHtml(
          q.question,
          furiganaMode,
          furiganaFirstOnly,
          knownKanji
        ),
        options: q.options.map((o) =>
          processStoryHtml(o, furiganaMode, furiganaFirstOnly, knownKanji)
        ),
      })),
    [questions, furiganaMode, furiganaFirstOnly, knownKanji]
  )

  const [answers, setAnswers] = useState<(number | null)[]>(() =>
    result ? [...result.answers] : questions.map(() => null)
  )
  const [flashPass, setFlashPass] = useState(false)

  const { mutate } = useMutation({
    mutationFn: (finalAnswers: (number | null)[]) =>
      submitQuiz(storyId, finalAnswers),
    onSuccess: (_data, finalAnswers) => {
      // Only the library-row indicators (completed/passed) depend on a refetch;
      // those only change once the quiz is fully answered.
      if (finalAnswers.every((a) => a !== null)) {
        queryClient.invalidateQueries({ queryKey: ["story", storyId] })
        queryClient.invalidateQueries({ queryKey: ["stories"] })
      }
    },
  })

  const onPick = (qIdx: number, optIdx: number) => {
    if (answers[qIdx] === optIdx) return
    const wasComplete = answers.every((a) => a !== null)
    const wasPassed =
      wasComplete && answers.every((a, i) => a === correctIndices[i])
    const next = [...answers]
    next[qIdx] = optIdx
    setAnswers(next)
    const nowPassed =
      next.every((a) => a !== null) &&
      next.every((a, i) => a === correctIndices[i])
    if (!wasPassed && nowPassed) {
      setFlashPass(true)
    }
    // Persist on every change so progress survives reloads.
    mutate(next)
  }

  const completed = answers.every((a) => a !== null)
  const quizPassed =
    completed && answers.every((a, i) => a === correctIndices[i])

  return (
    <>
      <div className="mt-10 mb-6">
        <p className="mb-1 text-sm tracking-widest text-muted-foreground uppercase">
          Quiz
        </p>
        <h2 className="font-heading text-3xl font-semibold tracking-tight text-foreground">
          クイズ
        </h2>
      </div>
      <Card
        onAnimationEnd={() => setFlashPass(false)}
        className={cn(
          completed
            ? quizPassed
              ? "border-2 border-success bg-success-soft/40"
              : "border-2 border-destructive bg-muted/40"
            : "bg-muted/40",
          flashPass && "animate-quiz-pass"
        )}
      >
        <CardContent className="space-y-10">
          {processedQuestions.map((q, qIdx) => {
            const picked = answers[qIdx]
            const correct = correctIndices[qIdx]
            const revealed = picked !== null
            return (
              <div key={qIdx} className="space-y-3">
                <p
                  className="leading-relaxed font-medium"
                  style={{
                    fontFamily: "var(--font-reading)",
                    fontSize: "clamp(16px, 3.5vw, 24px)",
                    lineHeight: "2em",
                  }}
                  dangerouslySetInnerHTML={{ __html: q.question }}
                />
                <div className="space-y-2">
                  {q.options.map((opt, optIdx) => {
                    const isPicked = picked === optIdx
                    const isCorrect = correct === optIdx
                    return (
                      <button
                        key={optIdx}
                        type="button"
                        onClick={() => onPick(qIdx, optIdx)}
                        className={`w-full cursor-pointer rounded-xl border px-4 py-3 text-left transition-all ${optionClass(
                          isPicked,
                          isCorrect,
                          revealed
                        )}`}
                        style={{
                          fontFamily: "var(--font-reading)",
                          fontSize: "clamp(13px, 2.8vw, 19px)",
                          lineHeight: "2em",
                        }}
                        dangerouslySetInnerHTML={{ __html: opt }}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })}
        </CardContent>
      </Card>
      <p className="mt-4 text-center text-sm text-muted-foreground">
        Make sure you keep your textbook progress up to date in{" "}
        <Link to="/settings" className="underline hover:text-foreground">
          settings
        </Link>
        .
      </p>
    </>
  )
}
